/**
 * Group Detail — W1a-10 — wired to live data via `useUserGroup` +
 * `useAddGroupMembers` + `useRemoveGroupMember`.
 *
 * Tabs:
 *   1. Members      — list of members + Add Members dialog + Remove
 *   2. Permissions  — the roles this GROUP holds, and the grants they hand
 *                     every member (B1-T4). Assign and remove live here.
 *
 * The W1a-09 detail-page scaffold had four tabs (Members, Roles Granted,
 * Resource Grants, Activity) backed by mock data. W1a-10 collapses those
 * to two tabs (Members + Permissions) — the "Roles Granted" tab assumed
 * a `group_role_assignments` mutation surface that v1 doesn't expose, and
 * the "Resource Grants" + "Activity" tabs were entirely mock-only. Each
 * is captured as a Wave-5 follow-up:
 *
 *   - F-W1a-10.2 — CLOSED by B1-T4: `group_role_assignments` has a writer
 *     (POST/DELETE /users/groups/:id/roles) and a reader
 *     (GET /users/groups/:id/permissions) that runs the SAME resolver path
 *     `resolvePrincipal` uses, so what this tab shows and what the gate
 *     enforces for a member cannot differ.
 *   - F-W1a-10.3 — Resource Grants tab (Wave 5 catalog-groups feature)
 *   - F-W1a-10.4 — Per-group Activity tab (the W1a-11 user-activity feed
 *     could be filtered by `resource_type='user_group' AND resource_id=:id`
 *     — small follow-up once W1a-11 lands)
 *
 * Edit + Delete reuse the W1a-09 mutations via the existing GroupEditorDialog
 * (which already accepts the live `UserGroupRow` shape).
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import {
  ChevronLeft,
  UsersRound,
  Shield,
  Users as UsersIcon,
  Pencil,
  MoreHorizontal,
  Trash2,
  Copy,
  Plus,
  Search,
  Eye,
  UserMinus,
  Loader2,
  Save,
  X as XIcon,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  useUserGroup,
  useUpdateUserGroup,
  useDeleteUserGroup,
  useAddGroupMembers,
  useRemoveGroupMember,
  useUsers,
  useRoles,
  useGroupPermissions,
  useAssignRoleToGroup,
  useRemoveRoleFromGroup,
  type UserGroupDetail,
  type UserGroupMember,
} from '@/lib/use-users';
import { useHasPermission } from '@/lib/use-has-permission';
import { UserAvatar } from '@/components/users/user-badges';

export const Route = createFileRoute('/users/groups/$groupId')({
  component: GroupDetailPage,
});

function GroupDetailPage() {
  const { groupId } = Route.useParams();
  const navigate = useNavigate();
  const { group, isLoading, error } = useUserGroup(groupId);
  const [tab, setTab] = useState('members');
  const [editorOpen, setEditorOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [addMembersOpen, setAddMembersOpen] = useState(false);

  const canWrite = useHasPermission('write', 'users');
  // The Permissions tab writes ROLE ASSIGNMENTS, and the API gates those on
  // `role:assign:users` — not on `write:users`. Gating the controls on the
  // same key the gate reads is what stops the screen offering a button that
  // answers 403 (the mirror-the-read-path rule).
  const canAssignRole = useHasPermission('role:assign', 'users');

  const deleteGroup = useDeleteUserGroup();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!group || error) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <UsersRound className="h-12 w-12 text-muted-foreground/30 mb-3" />
        <h2 className="text-sm font-semibold mb-1">Group not found</h2>
        <p className="text-xs text-muted-foreground mb-4">
          The group ID <code className="text-[11px]">{groupId}</code> doesn't
          exist or you don't have access.
        </p>
        <Button size="sm" onClick={() => navigate({ to: '/users/groups' as any })}>
          <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Back to Groups
        </Button>
      </div>
    );
  }

  const createdByName =
    group.createdBy.firstName || group.createdBy.lastName
      ? `${group.createdBy.firstName ?? ''} ${group.createdBy.lastName ?? ''}`.trim()
      : group.createdBy.email ?? 'Unknown';

  const handleDelete = async () => {
    try {
      await deleteGroup.mutateAsync({ groupId: group.id });
      toast.success(`Group "${group.name}" deleted`);
      navigate({ to: '/users/groups' as any });
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to delete group');
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b bg-background px-6 py-4">
        <button
          onClick={() => navigate({ to: '/users/groups' as any })}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3 transition-colors"
        >
          <ChevronLeft className="h-3 w-3" />
          Back to Groups
        </button>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="h-11 w-11 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
              <UsersRound className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-semibold truncate">{group.name}</h1>
                <Badge variant="outline" className="text-[10px] gap-1">
                  <UsersIcon className="h-2.5 w-2.5" />
                  {group.membershipType === 'dynamic' ? 'Dynamic' : 'Manual'}
                </Badge>
                <Badge variant="outline" className="text-[10px] tabular-nums">
                  {group.memberCount}{' '}
                  {group.memberCount === 1 ? 'member' : 'members'}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">
                {group.description ?? (
                  <span className="italic text-muted-foreground/70">
                    No description.
                  </span>
                )}
              </p>
              <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-2 tabular-nums flex-wrap">
                <span>
                  Created {new Date(group.createdAt).toLocaleDateString()}
                </span>
                <span>·</span>
                <span>
                  Updated {new Date(group.updatedAt).toLocaleDateString()}
                </span>
                <span>·</span>
                <span>Created by {createdByName}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {canWrite && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditorOpen(true)}
                  title="Edit group"
                >
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" title="More actions">
                      <MoreHorizontal className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <DropdownMenuItem
                      className="text-xs"
                      onSelect={() => {
                        navigator.clipboard.writeText(group.id);
                        toast.success('Group ID copied');
                      }}
                    >
                      <Copy className="h-3.5 w-3.5 mr-2" /> Copy group ID
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-xs text-red-600 dark:text-red-400"
                      onSelect={() => setConfirmDelete(true)}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete group
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <Tabs
          value={tab}
          onValueChange={setTab}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <div className="px-6 pt-1 border-b">
            <TabsList className="h-9">
              <TabsTrigger value="members" className="text-xs gap-1.5">
                <UsersIcon className="h-3.5 w-3.5" /> Members
                <Badge
                  variant="secondary"
                  className="ml-1 h-4 text-[9px] px-1 tabular-nums"
                >
                  {group.memberCount}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="permissions" className="text-xs gap-1.5">
                <Shield className="h-3.5 w-3.5" /> Permissions
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-auto">
            <TabsContent value="members" className="m-0 p-6">
              <MembersTab
                group={group}
                canWrite={canWrite}
                onOpenAddMembers={() => setAddMembersOpen(true)}
              />
            </TabsContent>
            <TabsContent value="permissions" className="m-0 p-6">
              <PermissionsTab group={group} canWrite={canAssignRole} />
            </TabsContent>
          </div>
        </Tabs>
      </div>

      <GroupEditorInlineDialog
        group={group}
        open={editorOpen}
        onOpenChange={setEditorOpen}
      />

      <AddMembersDialog
        group={group}
        open={addMembersOpen}
        onOpenChange={setAddMembersOpen}
      />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete group "{group.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {group.memberCount > 0
                ? `This group has ${group.memberCount} member${group.memberCount === 1 ? '' : 's'}. Deleting it will revoke any access this group provided. This cannot be undone.`
                : 'This group has no members. Deletion is safe and will be logged in the audit trail.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteGroup.isPending}
            >
              {deleteGroup.isPending ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              )}
              Delete group
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Members tab ──────────────────────────────────────────────────────────────

function MembersTab({
  group,
  canWrite,
  onOpenAddMembers,
}: {
  group: UserGroupDetail;
  canWrite: boolean;
  onOpenAddMembers: () => void;
}) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [confirmRemove, setConfirmRemove] = useState<UserGroupMember | null>(
    null,
  );

  const removeMember = useRemoveGroupMember();

  const filtered = useMemo(() => {
    if (!search.trim()) return group.members;
    const q = search.trim().toLowerCase();
    return group.members.filter((m) => {
      const fullName = `${m.firstName ?? ''} ${m.lastName ?? ''}`
        .trim()
        .toLowerCase();
      return (
        fullName.includes(q) ||
        m.email.toLowerCase().includes(q) ||
        (m.role ?? '').toLowerCase().includes(q)
      );
    });
  }, [group.members, search]);

  const handleRemove = async (m: UserGroupMember) => {
    try {
      await removeMember.mutateAsync({
        groupId: group.id,
        userId: m.userId,
      });
      toast.success(
        `${formatMemberName(m)} removed from "${group.name}"`,
      );
      setConfirmRemove(null);
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to remove member');
    }
  };

  return (
    <div className="space-y-4 max-w-6xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search members…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 pl-8 text-xs"
          />
        </div>
        {canWrite && (
          <Button size="sm" onClick={onOpenAddMembers} title="Add members">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add members
          </Button>
        )}
      </div>

      <div className="rounded-lg border bg-card overflow-auto">
        <table
          className="w-full text-xs border-collapse"
          style={{ tableLayout: 'fixed' }}
        >
          <colgroup>
            <col style={{ width: 'auto', minWidth: 240 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 160 }} />
            <col style={{ width: 200 }} />
            <col style={{ width: 90 }} />
          </colgroup>
          <thead className="bg-muted/50">
            <tr className="border-b">
              <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                Member
              </th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                Role
              </th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                Added
              </th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                Added by
              </th>
              <th className="px-2 py-2 text-right text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <div className="py-12 text-center">
                    <UsersIcon className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                    <p className="text-sm font-medium">
                      {search
                        ? 'No members match your search'
                        : 'No members yet'}
                    </p>
                    {!search && canWrite && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Click "Add members" to add users to this group.
                      </p>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              filtered.map((m) => (
                <tr
                  key={m.userId}
                  className="border-b hover:bg-muted/30 cursor-pointer"
                  onClick={() =>
                    navigate({
                      to: '/users/$userId' as any,
                      params: { userId: m.userId } as any,
                    })
                  }
                >
                  <td className="px-4 py-2.5 align-middle">
                    <div className="flex items-center gap-2 min-w-0">
                      <UserAvatar name={formatMemberName(m)} size="sm" />
                      <div className="min-w-0">
                        <div className="font-medium truncate">
                          {formatMemberName(m)}
                        </div>
                        <div className="text-[10px] text-muted-foreground truncate">
                          {m.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-2.5 align-middle">
                    {m.role ? (
                      <Badge
                        variant="outline"
                        className="text-[10px] capitalize"
                      >
                        {m.role}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-[11px]">
                        —
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2.5 align-middle text-muted-foreground tabular-nums text-[11px]">
                    {relativeTime(m.addedAt)}
                  </td>
                  <td className="px-2 py-2.5 align-middle text-muted-foreground truncate text-[11px]">
                    {m.addedBy.name ?? (
                      <span className="italic">Unknown</span>
                    )}
                  </td>
                  <td
                    className="px-2 py-2.5 align-middle text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-end gap-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        title="View user"
                        onClick={() =>
                          navigate({
                            to: '/users/$userId' as any,
                            params: { userId: m.userId } as any,
                          })
                        }
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      {canWrite && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          title="Remove from group"
                          onClick={() => setConfirmRemove(m)}
                        >
                          <UserMinus className="h-3.5 w-3.5 text-muted-foreground hover:text-red-600" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AlertDialog
        open={!!confirmRemove}
        onOpenChange={(open) => !open && setConfirmRemove(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove {confirmRemove ? formatMemberName(confirmRemove) : ''} from
              "{group.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will revoke the access this group provided to the user. The
              user will remain in the tenant. This action is logged in the
              audit trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmRemove && handleRemove(confirmRemove)}
              disabled={removeMember.isPending}
            >
              {removeMember.isPending ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <UserMinus className="h-3.5 w-3.5 mr-1.5" />
              )}
              Remove member
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Permissions tab — the roles this group holds ───────────────────────────
//
// WHAT THIS REPLACED, DELETED IN THE SAME CHANGE (B1-T4). This tab used to
// render an empty `group.permissions` array with the copy "Permissions
// inherited from member roles — see individual user details" and a note that
// the real aggregation was "on the roadmap (W1a-12 / Wave 5)". Both halves
// were wrong by then: a group does not inherit its members' permissions, it
// GRANTS them — `resolvePrincipal` has unioned `group_role_assignments` into
// every member's principal since B1-T2 — and the reason the array was empty
// was that nothing could ever write that table (its only INSERT sat behind an
// unregistered route file). The page said "not yet" about a feature whose
// store and resolver were both already live and whose writer was one endpoint
// away.
//
// Nothing here computes authority. The roles and the grant list come from
// `GET /users/groups/:id/permissions`, which projects the resolver's own
// answer, so this tab and the gate cannot disagree.

function PermissionsTab({
  group,
  canWrite,
}: {
  group: UserGroupDetail;
  /** Holds `role:assign:users` — the key the two write endpoints gate on. */
  canWrite: boolean;
}) {
  const { data, isLoading, error } = useGroupPermissions(group.id);
  const { roles: allRoles, isLoading: rolesLoading } = useRoles();
  const assignRole = useAssignRoleToGroup();
  const removeRole = useRemoveRoleFromGroup();
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');

  const held = data?.roles ?? [];
  const heldIds = new Set(held.map((r) => r.roleId));
  // A SHIPPED role is not offered here (review B1-T4-R1 / B1T4-02). It is the
  // MEMBERSHIP role — one per person, set on the user, written by the one
  // writer that keeps exactly one platform-role assignment per membership —
  // and `POST /users/groups/:id/roles` refuses it with 403
  // PLATFORM_ROLE_NOT_GROUP_ASSIGNABLE. This filtered on `retiredAt` alone, so
  // Owner, Admin and Power User sat in the Grant-role select: granting one
  // made every member of the group an Owner as far as `can()` was concerned,
  // while every screen that prints a person's role still said "member". The
  // tenant's own roles — including a clone of a shipped one — are what a group
  // carries.
  const assignable = allRoles.filter(
    (r) => r.kind === 'custom' && r.retiredAt === null && !heldIds.has(r.id),
  );

  const handleAssign = async () => {
    if (!selectedRoleId) return;
    try {
      await assignRole.mutateAsync({ groupId: group.id, roleId: selectedRoleId });
      toast.success('Role granted to the group', {
        description: 'Every member of this group now holds it.',
      });
      setSelectedRoleId('');
    } catch (err) {
      const error = err as Error & { response?: { error?: { message?: string } } };
      toast.error(
        error.response?.error?.message ?? error.message ?? 'Could not grant the role',
      );
    }
  };

  const handleRemove = async (roleId: string, roleName: string) => {
    try {
      await removeRole.mutateAsync({ groupId: group.id, roleId });
      toast.success(`Removed "${roleName}" from the group`, {
        description: 'Members lose the grants it carried, unless they hold them another way.',
      });
    } catch (err) {
      const error = err as Error & { response?: { error?: { message?: string } } };
      toast.error(
        error.response?.error?.message ?? error.message ?? 'Could not remove the role',
      );
    }
  };

  return (
    <div className="space-y-4 max-w-5xl">
      <Card>
        <CardHeader className="pb-2 pt-4">
          <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
            <Shield className="h-3.5 w-3.5" />
            Roles this group grants ({held.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading ? (
            <p className="text-xs text-muted-foreground italic">Loading roles…</p>
          ) : error != null ? (
            <p className="text-xs text-red-600">
              Could not load this group&apos;s roles, so what it grants cannot be shown.
              Reload the page to try again.
            </p>
          ) : held.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">
              This group grants nothing yet. Give it a role and every member holds
              that role&apos;s permissions.
            </p>
          ) : (
            <div className="space-y-1">
              {held.map((r) => (
                <div
                  key={r.roleId}
                  className="flex items-center gap-3 rounded-md border p-2 text-xs"
                >
                  <Shield className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="font-medium">{r.roleName}</span>
                  <Badge variant="outline" className="text-[9px]">
                    {r.kind === 'platform' ? 'Platform' : 'Custom'}
                  </Badge>
                  <Badge variant="outline" className="text-[9px] tabular-nums">
                    {r.permissionCount} permissions
                  </Badge>
                  {canWrite && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-auto h-7 text-[11px]"
                      disabled={removeRole.isPending}
                      onClick={() => handleRemove(r.roleId, r.roleName)}
                    >
                      <XIcon className="h-3 w-3 mr-1" /> Remove
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {canWrite && (
            <div className="flex items-center gap-2 pt-1">
              <select
                aria-label="Role to grant this group"
                className="h-8 rounded-md border bg-background px-2 text-xs"
                value={selectedRoleId}
                onChange={(e) => setSelectedRoleId(e.target.value)}
                disabled={rolesLoading || assignable.length === 0}
              >
                <option value="">
                  {assignable.length > 0
                    ? 'Choose a role…'
                    : allRoles.some((r) => r.kind === 'custom')
                      ? 'No role left to grant'
                      : 'No tenant role to grant yet — create one on Roles'}
                </option>
                {assignable.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} ({r.permissionCount})
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                className="h-8 text-xs"
                disabled={!selectedRoleId || assignRole.isPending}
                onClick={handleAssign}
              >
                {assignRole.isPending ? 'Granting…' : 'Grant role'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 pt-4">
          <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
            <Shield className="h-3.5 w-3.5" />
            What every member gets ({data?.permissions.length ?? 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(data?.permissions.length ?? 0) === 0 ? (
            <p className="text-xs text-muted-foreground italic">
              No grants — this group hands its members nothing of its own.
            </p>
          ) : (
            <div className="space-y-1">
              {data!.permissions.map((p) => (
                <div
                  key={p.key}
                  className="flex items-center gap-3 rounded-md border p-2 text-xs"
                >
                  <Shield className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <code className="text-[11px] font-mono">{p.key}</code>
                  <Badge variant="outline" className="text-[9px] ml-auto">
                    {p.source}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground">
        <strong className="text-foreground">How this works:</strong> a role granted
        to a group is held by every member of that group — the same resolver that
        answers &quot;what may this person do?&quot; reads these assignments, so a
        member gains the grants the moment they join and loses them when they leave.
        Their own directly-assigned roles are on their user page.
      </div>
    </div>
  );
}

// ─── Edit-group dialog (inline; reuses the W1a-09 edit mutation) ─────────────

function GroupEditorInlineDialog({
  group,
  open,
  onOpenChange,
}: {
  group: UserGroupDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description ?? '');
  const updateGroup = useUpdateUserGroup();

  // Reset on open.
  useMemo(() => {
    if (open) {
      setName(group.name);
      setDescription(group.description ?? '');
    }
  }, [open, group.name, group.description]);

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error('Name cannot be empty');
      return;
    }
    try {
      await updateGroup.mutateAsync({
        groupId: group.id,
        body: {
          name: trimmedName,
          description: description.trim() || null,
        },
      });
      toast.success(`Group "${trimmedName}" updated`);
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to update group');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit group</DialogTitle>
          <DialogDescription>
            Rename or rewrite the description. Member management lives on the
            Members tab.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="group-name" className="text-xs">
              Name
            </Label>
            <Input
              id="group-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              className="h-8 text-xs mt-1"
            />
          </div>
          <div>
            <Label htmlFor="group-desc" className="text-xs">
              Description
            </Label>
            <Textarea
              id="group-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={1000}
              rows={3}
              className="text-xs mt-1"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updateGroup.isPending}>
            {updateGroup.isPending ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5 mr-1.5" />
            )}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Add-members dialog ──────────────────────────────────────────────────────

function AddMembersDialog({
  group,
  open,
  onOpenChange,
}: {
  group: UserGroupDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { users } = useUsers({ pageSize: 200 });
  const addMembers = useAddGroupMembers();

  // Build a quick set of existing member ids so the picker can hide them.
  const existingIds = useMemo(
    () => new Set(group.members.map((m) => m.userId)),
    [group.members],
  );

  const candidates = useMemo(() => {
    const filtered = (users ?? []).filter((u) => !existingIds.has(u.id));
    if (!search.trim()) return filtered;
    const q = search.trim().toLowerCase();
    return filtered.filter((u) => {
      const full = (u.fullName ?? '').toLowerCase();
      const email = (u.email ?? '').toLowerCase();
      return full.includes(q) || email.includes(q);
    });
  }, [users, existingIds, search]);

  // Reset on open.
  useMemo(() => {
    if (open) {
      setSearch('');
      setSelectedIds(new Set());
    }
  }, [open]);

  const toggle = (userId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const handleAdd = async () => {
    if (selectedIds.size === 0) {
      toast.error('Select at least one user');
      return;
    }
    try {
      const result = await addMembers.mutateAsync({
        groupId: group.id,
        body: { userIds: Array.from(selectedIds) },
      });
      const parts: string[] = [];
      if (result.added.length > 0)
        parts.push(
          `${result.added.length} added`,
        );
      if (result.alreadyMembers.length > 0)
        parts.push(
          `${result.alreadyMembers.length} already member${result.alreadyMembers.length === 1 ? '' : 's'}`,
        );
      toast.success(parts.join(', '));
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to add members');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add members to "{group.name}"</DialogTitle>
          <DialogDescription>
            Select users to add to this group. Users already in the group
            don't appear.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search users…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-8 text-xs"
            />
          </div>
          <div className="max-h-72 overflow-auto rounded-md border bg-card">
            {candidates.length === 0 ? (
              <div className="py-8 text-center text-xs text-muted-foreground">
                {search
                  ? 'No users match your search'
                  : 'All tenant users are already in this group'}
              </div>
            ) : (
              candidates.map((u) => {
                const checked = selectedIds.has(u.id);
                const display = u.fullName || u.email;
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => toggle(u.id)}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-2 text-left border-b last:border-b-0 transition-colors',
                      'hover:bg-muted/50',
                      checked && 'bg-primary/5',
                    )}
                  >
                    <UserAvatar name={display} size="sm" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium truncate">
                        {display}
                      </div>
                      <div className="text-[10px] text-muted-foreground truncate">
                        {u.email}
                      </div>
                    </div>
                    {checked && (
                      <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>
          {selectedIds.size > 0 && (
            <div className="text-[11px] text-muted-foreground tabular-nums flex items-center gap-2">
              <span>
                {selectedIds.size} user{selectedIds.size === 1 ? '' : 's'}{' '}
                selected
              </span>
              <button
                type="button"
                className="hover:underline inline-flex items-center gap-0.5"
                onClick={() => setSelectedIds(new Set())}
              >
                <XIcon className="h-2.5 w-2.5" /> clear
              </button>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={addMembers.isPending || selectedIds.size === 0}
          >
            {addMembers.isPending ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Plus className="h-3.5 w-3.5 mr-1.5" />
            )}
            Add {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatMemberName(m: UserGroupMember): string {
  const full = `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim();
  return full.length > 0 ? full : m.email;
}

function relativeTime(iso: string | null): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (minutes < 60) return minutes < 1 ? 'Just now' : `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}
